import {
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { PlatformFormsModule, SharedUiModule } from '@pf/shared-ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectOption } from '@pf/shared-common';
import {
    ZebraBrowserPrintService,
    ZebraPrintZplOptions,
} from '../services/zebra-browser-print.service';
import { PrinterPreferencesService } from '../services/printer-preferences.service';

export interface PrintSettings {
    copies: number;
    options: ZebraPrintZplOptions;
}

export interface PrintSettingsChangeEvent extends PrintSettings {
    valid: boolean;
    remember: boolean;
    autoPrint: boolean;
}

@UntilDestroy()
@Component({
    selector: 'platform-print-label-settings',
    standalone: true,
    imports: [PlatformFormsModule, SharedUiModule],
    templateUrl: './print-label-settings.component.html',
    styleUrl: './print-label-settings.component.scss',
})
export class PrintLabelSettingsComponent {
    private readonly zebraBrowserPrintService = inject(
        ZebraBrowserPrintService
    );
    private readonly printerPreferences = inject(PrinterPreferencesService);

    form = new FormGroup({
        copies: new FormControl(1, [Validators.min(1), Validators.max(5)]),
        printer: new FormControl('', [
            Validators.pattern(/^(\d{1,3}\.){3}\d{1,3}$/),
        ]),
        printerSelect: new FormControl<string | null>(null),
        remember: new FormControl<boolean>(false),
        autoPrint: new FormControl<boolean>(false),
    });
    @Input() labelCount = 0;
    @Output() settingsChanged = new EventEmitter<PrintSettingsChangeEvent>();

    availablePrinters: SelectOption[] = [];

    readonly hasPrinter = signal(false);
    readonly remembered = signal(false);
    readonly committedPrinter = signal(false);

    get copies(): number {
        const value = this.form?.value?.copies;
        return typeof value === 'number' && value > 0 ? value : 1;
    }

    get valid(): boolean {
        return this.form.valid && this.hasPrinter();
    }

    get totalLabels(): number {
        return this.labelCount * this.copies;
    }

    get remember(): boolean {
        return !!this.form.value.remember;
    }

    get autoPrint(): boolean {
        return !!this.form.value.autoPrint;
    }

    get showPrinterSelectors(): boolean {
        return (
            !this.hasPrinter() ||
            !this.remembered() ||
            !this.committedPrinter()
        );
    }

    get selectedPrinterLabel(): string {
        const uid = this.form.value.printerSelect;
        const address = this.form.value.printer;
        if (uid) {
            const match = this.availablePrinters.find(p => p.value === uid);
            return match?.label?.toString() ?? uid;
        }
        return address ?? '';
    }

    constructor() {
        const prefs = this.printerPreferences.get();
        this.form.patchValue(
            {
                copies: prefs.copies || 1,
                printer: prefs.printerAddress || '',
                printerSelect: prefs.printerUid || null,
                remember: prefs.remember,
                autoPrint: prefs.autoPrint,
            },
            { emitEvent: false }
        );
        this.hasPrinter.set(this.computeHasPrinter());
        this.remembered.set(this.remember);
        this.committedPrinter.set(this.hasPrinter());

        this.zebraBrowserPrintService
            .getAvailablePrinters()
            .pipe(untilDestroyed(this))
            .subscribe(printers => {
                this.availablePrinters = printers.map(p => ({
                    label: p.name,
                    value: p.uid,
                }));
            });

        this.form.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.handleFormChange());
    }

    changePrinter(): void {
        this.committedPrinter.set(false);
        this.form.patchValue({
            printer: '',
            printerSelect: null,
            remember: false,
            autoPrint: false,
        });
    }

    protected onPrinterSelected(option: SelectOption) {
        if (!option.value) {
            return;
        }
        this.form.get('printer')?.reset();
        this.committedPrinter.set(true);
    }

    protected onPrinterInput(input: string) {
        if (!input) {
            return;
        }
        this.form.get('printerSelect')?.reset();
    }

    private handleFormChange(): void {
        const prevHasPrinter = this.hasPrinter();
        const nowHasPrinter = this.computeHasPrinter();

        if (nowHasPrinter && !prevHasPrinter) {
            this.form.patchValue(
                { remember: true, autoPrint: true },
                { emitEvent: false }
            );
        }

        if (!nowHasPrinter && (this.remember || this.autoPrint)) {
            this.form.patchValue(
                { remember: false, autoPrint: false },
                { emitEvent: false }
            );
        }

        this.hasPrinter.set(nowHasPrinter);
        this.remembered.set(this.remember);
        this.emitSettings();
    }

    private computeHasPrinter(): boolean {
        return !!(this.form.value.printer || this.form.value.printerSelect);
    }

    private emitSettings() {
        const printerAddress = this.form.value?.printer || '';
        const printerUid = this.form.value?.printerSelect || '';

        this.printerPreferences.save({
            printerAddress,
            printerUid,
            copies: this.copies,
            remember: this.remember,
            autoPrint: this.autoPrint,
        });

        this.settingsChanged.emit({
            copies: this.copies,
            valid: this.valid,
            remember: this.remember,
            autoPrint: this.autoPrint,
            options: {
                printerAddress,
                printerUid,
            },
        });
    }
}
